<template>
    <ul class="list list-unstyled tw-m-0">
        <li v-if="items.length === 0" key="-1" class="tw-p-8 tw-text-center">
            {{ $t("meetings.analytics.userlist.noContent") }}
        </li>
        <li
            v-for="(item, index) in items"
            :key="`user_${item.user._id}`"
            class="tw-flex tw-flex-col tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 tw-rounded-md"
            @mouseenter="onHover(item.user._id, index)"
            @focus="onHover(item.user._id, index)"
        >
            <div
                class="tw-flex tw-flex-col tw-space-y-5 xl:tw-flex-row xl:tw-space-x-8 xl:tw-space-y-0 tw-items-center tw-py-5 tw-transition-all tw-duration-300 hover:tw-px-2 tw-rounded-md"
                :class="{
                    'tw-px-2 tw-bg-gray-50': clickedIndex === index,
                    'tw-cursor-pointer': item.stats.amountTotal,
                }"
                @click.stop="clickedIndex = clickedIndex === index ? null : index"
                @keyup.enter="clickedIndex = clickedIndex === index ? null : index"
            >
                <div class="tw-w-72 tw-flex tw-items-center tw-space-x-4">
                    <l-profile-picture size="m" :user="item.user" />
                    <div class="tw-max-w-full">
                        <a
                            :href="`#/profile/${item.user._id}`"
                            target="_blank"
                            class="tw-text-gray-600  tw-truncate tw-font-medium tw-leading-6 tw-space-y-1"
                            @click.stop
                        >
                            {{ item.user.fullname }}
                        </a>
                        <br />
                        <a
                            v-show="hoveringIndex === index && item.stats.amountTotal"
                            class="tw-inline-block tw-text-xs tw-border tw-border-solid tw-border-indigo-100 tw-rounded tw-py-1 tw-px-2 tw-mt-2 tw-leading-none"
                            @click.stop.prevent="
                                clickedIndex = clickedIndex === index ? null : index
                            "
                            @keyup.enter="clickedIndex = clickedIndex === index ? null : index"
                        >
                            <i
                                class="tw-align-baseline tw-mr-2"
                                :class="[
                                    clickedIndex === index
                                        ? 'fa fa-caret-down'
                                        : 'fa fa-caret-right',
                                ]"
                                aria-hidden="true"
                            />
                            <span>{{ $t("meetings.analytics.userlist.showDetails") }}</span>
                        </a>
                    </div>
                </div>

                <div class="tw-flex-1"></div>

                <div class="tw-flex-grow-0 tw-text-xs">
                    <div>
                        <div>
                            <span
                                class="tw-py-1 tw-px-2 tw-mr-1 tw-text-center tw-w-11 tw-rounded-md tw-bg-gray-200 tw-text-gray-500 tw-font-bold tw-inline-block"
                                >{{ item.stats.amountTotal }}</span
                            >
                            {{ $t("meetings.analytics.userlist.labelMeetings") }}
                        </div>
                        <div
                            v-b-popover.hover.top="`Based on action items and notes added.`"
                            class="tw-mt-4 tw-inline-block"
                        >
                            <span
                                class="tw-py-1 tw-px-2 tw-mr-1 tw-text-center tw-w-11 tw-rounded-md tw-bg-indigo-500 tw-text-indigo-50 tw-font-bold tw-inline-block"
                                >{{
                                    String(item.stats.amountLikelyHeld).padStart(
                                        String(item.stats.amountTotal).length,
                                        "0",
                                    )
                                }}</span
                            >
                            {{ $t("meetings.analytics.userlist.labelActiveMeetings") }}
                        </div>
                    </div>
                </div>

                <div
                    class="tw-w-80 tw-h-16 tw-bg-gray-50 tw-rounded-md tw-transition-colors tw-duration-300 tw-flex tw-items-center tw-justify-center"
                    :class="{ 'tw-bg-gray-100': hoveringIndex === index }"
                >
                    <l-chart
                        v-if="
                            userDetails[item.user._id] &&
                                userDetails[item.user._id].timelines.timelineComplete.length > 5 &&
                                hoveringIndex === index
                        "
                        :options="previewChartOptions"
                        :colors="['grey']"
                        :data="{
                            labels: [],
                            datasets: [
                                {
                                    data: fillTimeSeriesData(
                                        userDetails[item.user._id].timelines.timelineComplete,
                                        $moment,
                                        60,
                                    ),
                                },
                            ],
                        }"
                        type="line"
                        height="64"
                    />
                    <div
                        v-else-if="hoveringIndex === index"
                        class="tw-text-xs tw-font-bold tw-text-gray-400"
                    >
                        <svg
                            v-if="isLoadingDetails"
                            class="tw-animate-spin tw-h-5 tw-w-5 tw-text-gray-200"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="tw-opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="tw-opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <div v-else>{{ $t("meetings.analytics.userlist.notEnoughData") }}</div>
                    </div>
                </div>
            </div>

            <div
                v-if="clickedIndex === index && item.stats.amountTotal"
                class="tw-w-full tw-flex-1 tw-bg-gray-100"
            >
                <div v-if="isLoadingDetails && !userDetails[item.user._id]" class="tw-text-center">
                    <svg
                        class="tw-animate-spin tw-h-5 tw-w-5 tw-text-gray-200"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="tw-opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="tw-opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                </div>
                <l-meeting-analytics-user-list-details
                    v-else-if="userDetails[item.user._id]"
                    :user="userDetails[item.user._id]"
                    :time-unit="timeUnit"
                />
            </div>
        </li>
    </ul>
</template>

<script>
import { mapGetters } from "vuex";
import LMeetingAnalyticsUserListDetails from "@/components/LMeetingAnalytics/LMeetingAnalyticsUserListDetails";

import LProfilePicture from "@/components/LProfilePicture";
import LChart from "@/components/LChart";

import fillTimeSeriesData from "@/utils/fill-timeseries-data";

export default {
    components: {
        LMeetingAnalyticsUserListDetails,
        LProfilePicture,
        LChart,
    },
    props: {
        timeUnit: {
            type: String,
            default: "month",
        },
        isLoadingDetails: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            hoveringIndex: null,
            clickedIndex: null,
            previewChartOptions: Object.freeze({
                responsive: true,
                maintainAspectRatio: false,
                fullWidth: true,
                title: {
                    display: false,
                },
                legend: {
                    display: false,
                },
                elements: {
                    point: { radius: 0 },
                },
                tooltips: {
                    enabled: false,
                },
                scales: {
                    xAxes: [
                        {
                            type: "time",
                            display: false,
                            offset: true,
                        },
                    ],
                    yAxes: [
                        {
                            display: false,
                            ticks: {
                                beginAtZero: true,
                                min: 0,
                            },
                        },
                    ],
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 8,
                        bottom: 8,
                    },
                },
            }),
        };
    },
    computed: {
        ...mapGetters({
            userDetails: "meetings/stats/userDetails",
        }),
    },
    methods: {
        onHover(id, index) {
            this.hoveringIndex = index;
            this.$emit("loadUserDetails", id);
        },
        fillTimeSeriesData,
    },
};
</script>
